/**
 * callHistoryReducer.js
 * Talkroute
 *
 * Created by Vel Genov on 1/9/19
 * Copyright (c) 2019 Talkroute. All right reserved.
 */
import { REHYDRATE } from "redux-persist";
import moment from "moment";

import {
  FETCH_CALL_HISTORY_BEGIN,
  FETCH_CALL_HISTORY_FAILURE,
  FETCH_CALL_HISTORY_EMPTY,
  SHOW_CALL_DETAILS,
  APPLY_CALL_HISTORY_FILTER,
  callHistoryFilterHelper,
  HIDE_CALL_DETAILS,
  sortCallHistory,
  SET_CALL_HISTORY,
  ISetCallHistoryParams,
  countMissedCalls,
  ENTER_CALL_HISTORY,
  LEAVE_CALL_HISTORY,
  CLEAR_MISSED_COUNT,
} from "../actions/callHistoryActions";
import { CLEAR_ERRORS } from "../actions/errorActions";
import dataStateEnum from "../constants/dataStateConstants";
import uiString from "../constants/uiString";
import {
  callHistoryRehydrator,
  callHistoryRehydrateCall,
  CallHistoryData,
} from "../services/dataClasses/CallHistoryData";
import { IPagination } from "@models/IPagination";

export interface  ICallHistoryReducer {
  data: CallHistoryData[];
  dataState: dataStateEnum;
  lastFetchTime: null | number;
  error: null | string;
  filterBy: string;
  callDetails: CallHistoryData | null;
  callDetailsParent: null | object;
  relatedCalls: CallHistoryData[] | null;
  callHistoryPagination: null | IPagination;
  missedCount: number;
  isUserOnCallHistoryComponent: boolean;
}

const initialState = {
  data: [],
  dataState: dataStateEnum.LOADING,
  lastFetchTime: null,
  error: null,
  filterBy: uiString.ALL_CALLS,
  callDetails: null,
  callDetailsParent: null,
  relatedCalls: null,
  callHistoryPagination: null,
  missedCount: 0,
  isUserOnCallHistoryComponent: false,
};

/**
 * A redux function to process callHistory actions
 * @param {*} state the redux state
 * @param {*} action passed in action through the callHistoryAction creator
 */
export default function callHistory(state: ICallHistoryReducer = initialState, action): ICallHistoryReducer {
  switch (action.type) {
    case REHYDRATE:
      // A redux-persist action. Called immediately after launch. CallHistoryData objects are restored as generic Object by default.
      // Using a rehydrator function to restore to CallHistoryData objects. Rehydrator checks for empty objects.
      if (action.payload !== undefined) {
        return {
          ...state,
          data: callHistoryRehydrator(action.payload.callHistory.data),
          relatedCalls: callHistoryRehydrator(
            action.payload.callHistory.relatedCalls
          ),
          callDetails: callHistoryRehydrateCall(
            action.payload.callHistory.callDetails
          )
        };
      }

      return {
        ...state,
      };

    case FETCH_CALL_HISTORY_BEGIN:
      // Mark the state as LOADING so we can show a the user data is being loaded
      // Also, reset any errors. We're starting fresh.
      return {
        ...state,
        callDetails: null,
        dataState: dataStateEnum.LOADING,
        error: null,
      };

    case SET_CALL_HISTORY: {
      const {data, pagination, callHistoryLastViewed } = action.payload as ISetCallHistoryParams;
      const currentTimestamp = moment().unix();
      const totalMissedCount = countMissedCalls(data, callHistoryLastViewed);

      return {
        ...state,
        dataState: dataStateEnum.READY,
        data: data,
        callHistoryPagination: pagination ?? null,
        missedCount: state.isUserOnCallHistoryComponent ? 0 : totalMissedCount,
        lastFetchTime: currentTimestamp,
        error: null,
      };
    }

    case FETCH_CALL_HISTORY_FAILURE:
      // An error occured while loading the data
      return {
        ...state,
        dataState: dataStateEnum.ERROR,
        error: action.payload.error,
      };

    case FETCH_CALL_HISTORY_EMPTY:
      // An empty redux action. Use the previous state
      return {
        ...state,
      };
    case ENTER_CALL_HISTORY:
      return {
        ...state,
        isUserOnCallHistoryComponent: true,
      };
    case SHOW_CALL_DETAILS:
      // Shows details about a call
      return {
        ...state,
        callDetails: action.payload.call,
        relatedCalls: action.payload.topRelatedCalls,
        callDetailsParent: action.payload.parent,
      };
    case LEAVE_CALL_HISTORY:
        return {
          ...state,
          isUserOnCallHistoryComponent: false,
        };
    case HIDE_CALL_DETAILS:
      // Hides details about a call
      return {
        ...state,
        callDetails: action.payload.call,
        relatedCalls: action.payload.topRelatedCalls,
        callDetailsParent: action.payload.parent,
      };

    case APPLY_CALL_HISTORY_FILTER:
      // Apply the Call History filter
      return {
        ...state,
        filterBy: action.payload.filterBy,
      };
    case CLEAR_MISSED_COUNT:
        return {
          ...state,
          missedCount: 0,
        };
    case CLEAR_ERRORS:
      // Clear any errors
      return {
        ...state,
        error: null,
      };
    default:
      // ALWAYS have a default case in a reducer
      return state;
  }
}
